import Phaser from 'phaser';

import { getCheckpointScoreBandBoundaries } from '../objects/StrokeEvaluator';

import type { StrokeEvaluator, DirectionalCheckpoint } from '../objects/StrokeEvaluator';

type StrokeDebugData = {
	readonly checkpoints: readonly Readonly<DirectionalCheckpoint>[];
	readonly progress: Pick<StrokeEvaluator, 'getInactiveThrough' | 'getCheckpointScore' | 'isScoringCheckpoint'>;
};

type StrokeDebugRendererConfig = {
	scene: Phaser.Scene;
	parent: Phaser.GameObjects.Container;
	strokes: readonly StrokeDebugData[];
	checkpointMaxDistance: number;
};

const DEBUG_ACTIVE_COLOR = 0x00ffff;
const DEBUG_TARGET_COLOR = 0xaaffaa;
const DEBUG_INACTIVE_COLOR = 0x555555;
const DEBUG_SKIPPED_COLOR = 0xdd8844;
const DEBUG_LINE_WIDTH = 3;
const DEBUG_BAND_TICK_LENGTH = 16;
const DEBUG_LABEL_OFFSET = 18;
const DEBUG_LABEL_STYLE: Phaser.Types.GameObjects.Text.TextStyle = {
	backgroundColor: '#000000cc',
	color: '#ffffff',
	fontFamily: 'Arial, sans-serif',
	fontSize: '20px',
	padding: { x: 4, y: 2 },
};

const formatDebugValue = (value: number) => (Number.isInteger(value) ? String(value) : value.toFixed(2));

export class StrokeDebugRenderer {
	private readonly strokes: readonly StrokeDebugData[];
	private readonly checkpointMaxDistance: number;
	private readonly debugGraphics: Phaser.GameObjects.Graphics;
	private readonly debugLabels: (Phaser.GameObjects.Text | undefined)[][];

	constructor({ scene, parent, strokes, checkpointMaxDistance }: StrokeDebugRendererConfig) {
		this.strokes = strokes;
		this.checkpointMaxDistance = checkpointMaxDistance;
		this.debugGraphics = scene.add.graphics();
		parent.add(this.debugGraphics);
		this.debugLabels = this.createDebugLabels(scene, parent);
	}

	render(activeStrokeIndex: number) {
		const graphics = this.debugGraphics;

		graphics.clear();
		const scoreBandBoundaries = getCheckpointScoreBandBoundaries(this.checkpointMaxDistance);
		this.strokes.forEach(({ checkpoints, progress }, strokeIndex) => {
			checkpoints.forEach(({ point, tangent }, checkpointIndex) => {
				const label = this.debugLabels[strokeIndex][checkpointIndex];
				if (!progress.isScoringCheckpoint(checkpointIndex)) {
					label?.setVisible(false);
					return;
				}
				const tangentVector = new Phaser.Math.Vector2(tangent.x, tangent.y).normalize();
				const normalVector = new Phaser.Math.Vector2(-tangentVector.y, tangentVector.x);
				const normal = normalVector.clone().scale(this.checkpointMaxDistance);
				const forward = tangentVector.clone().scale(this.checkpointMaxDistance * 0.5);
				const arrowNormal = normalVector.clone().scale(scoreBandBoundaries[0]);
				const bandTickHalf = tangentVector.clone().scale(DEBUG_BAND_TICK_LENGTH * 0.5);
				const inactive =
					strokeIndex < activeStrokeIndex ||
					(strokeIndex === activeStrokeIndex && checkpointIndex <= progress.getInactiveThrough());
				const { status, score } = progress.getCheckpointScore(checkpointIndex);
				const isTarget =
					strokeIndex === activeStrokeIndex && checkpointIndex === progress.getInactiveThrough() + 1;
				const color =
					status === 'skipped'
						? DEBUG_SKIPPED_COLOR
						: inactive
						? DEBUG_INACTIVE_COLOR
						: isTarget
						? DEBUG_TARGET_COLOR
						: DEBUG_ACTIVE_COLOR;

				if (inactive && label) {
					label
						.setText(status === 'skipped' ? 'skipped 0' : `score ${formatDebugValue(score)}`)
						.setVisible(true);
				} else {
					label?.setVisible(false);
				}

				graphics
					.lineStyle(DEBUG_LINE_WIDTH, color)
					.lineBetween(point.x - normal.x, point.y - normal.y, point.x + normal.x, point.y + normal.y)
					.lineBetween(
						point.x - arrowNormal.x,
						point.y - arrowNormal.y,
						point.x + forward.x,
						point.y + forward.y,
					)
					.lineBetween(
						point.x + arrowNormal.x,
						point.y + arrowNormal.y,
						point.x + forward.x,
						point.y + forward.y,
					);

				scoreBandBoundaries.forEach((distance) => {
					[-1, 1].forEach((direction) => {
						const tickCenterX = point.x + normalVector.x * distance * direction;
						const tickCenterY = point.y + normalVector.y * distance * direction;

						graphics.lineBetween(
							tickCenterX - bandTickHalf.x,
							tickCenterY - bandTickHalf.y,
							tickCenterX + bandTickHalf.x,
							tickCenterY + bandTickHalf.y,
						);
					});
				});
			});
		});
	}

	private createDebugLabels(scene: Phaser.Scene, parent: Phaser.GameObjects.Container) {
		return this.strokes.map(({ checkpoints, progress }) =>
			checkpoints.map(({ point }, index) => {
				if (!progress.isScoringCheckpoint(index)) return undefined;
				const label = scene.add
					.text(point.x + DEBUG_LABEL_OFFSET, point.y + DEBUG_LABEL_OFFSET, '', DEBUG_LABEL_STYLE)
					.setVisible(false);

				parent.add(label);

				return label;
			}),
		);
	}
}
